import ExpandableSection from '@amzn/awsui-components-react/polaris/expandable-section';
import TextContent from '@amzn/awsui-components-react/polaris/text-content';
import { useIntl } from 'react-intl';

import styles from './HelpTopics.module.css';
import { AppConfig } from '../types/app';

const HelpTopicExpandable = ({
  question,
  answer,
  config,
  as: Wrapper = 'li',
}: {
  question: Record<string, any>;
  answer: Record<string, any>;
  config: AppConfig;
  /**
   * Element used to wrap the topic. Defaults to `li` for use inside a
   * semantic list. Pass `div` when rendering a single, standalone topic so
   * that screen readers don't announce misleading "list, 1 item" semantics
   * (WCAG 2.1 SC 1.3.1 Info and Relationships).
   */
  as?: 'li' | 'div';
}) => {
  const { formatMessage, formatDate } = useIntl();

  return (
    <Wrapper className={styles.item}>
      <ExpandableSection
        header={
          <TextContent>
            <h3 className={styles.itemHeader} data-testid="help-topic">
              {formatMessage(question)}
            </h3>
          </TextContent>
        }
      >
        <TextContent className={styles.content}>
          {formatMessage(answer, {
            // These makes sure our help content can have some formatted content.
            //Lwa Sunset date us December 4th, 2023
            endDate: formatDate(config.lwaSunsetDate, {
              dateStyle: 'long',
            }),
            ol: (str) => <ol>{str}</ol>,
            ul: (str) => <ul>{str}</ul>,
            li: (str) => <li>{str}</li>,
            h4: (str) => <h4>{str}</h4>,
            b: (str) => <b>{str}</b>,
            p: (str) => <p>{str}</p>,
            i: (str) => <i>{str}</i>,
            hyperlink: (str) => (
              <a
                href={str.toString()}
                target="_blank"
                rel="noopener noreferrer"
              >
                {str}
              </a>
            ),
            supportLink: (str) => (
              <a
                href="https://support.aws.amazon.com/#/contacts/aws-training"
                target="_blank"
                rel="noopener noreferrer"
              >
                {str}
              </a>
            ),
            table: (str) => (
              <table className={styles.tableBorders}>{str}</table>
            ),
            tr: (str) => <tr>{str}</tr>,
            th: (str) => <th className={styles.tableBorders}>{str}</th>,
            td: (str) => <td className={styles.tableBorders}> {str} </td>,
            builderID: (str) => (
              <a
                href="https://docs.aws.amazon.com/signin/latest/userguide/sign-in-aws_builder_id.html?icmpid=docs_builder_id_console"
                target="_blank"
                rel="noopener noreferrer"
              >
                {str}
              </a>
            ),
            awsBuilderIDAwsCred: (str) => (
              <a
                href="https://docs.aws.amazon.com/signin/latest/userguide/differences-aws_builder_id.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                {str}
              </a>
            ),
            amazonRetail: (str) => (
              <a
                href="http://amazon.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {str}
              </a>
            ),
            apn: (str) => (
              <a
                href="https://partnercentral.awspartner.com/APNSelfRegister"
                target="_blank"
                rel="noopener noreferrer"
              >
                {str}
              </a>
            ),
            builderIDSignInIssue: (str) => (
              <a
                href="https://docs.aws.amazon.com/signin/latest/userguide/troubleshooting-sign-in-issues.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                {str}
              </a>
            ),
          })}
        </TextContent>
      </ExpandableSection>
    </Wrapper>
  );
};

const HelpTopics = ({
  helpTopics,
  config,
}: {
  helpTopics: { question: Record<string, any>; answer: Record<string, any> }[];
  config: AppConfig;
}) => {
  // A list with a single item misrepresents the content structure to
  // assistive technology (screen readers announce "list, 1 item"). Only use
  // list semantics when grouping two or more related topics; render a single
  // standalone topic without list markup (WCAG 2.1 SC 1.3.1).
  if (helpTopics.length === 1) {
    const { question, answer } = helpTopics[0];
    return (
      <div className={styles.list}>
        <HelpTopicExpandable
          as="div"
          question={question}
          answer={answer}
          config={config}
        />
      </div>
    );
  }

  return (
    <ul className={styles.list}>
      {helpTopics.map(({ question, answer }, i) => (
        <HelpTopicExpandable
          key={i}
          question={question}
          answer={answer}
          config={config}
        />
      ))}
    </ul>
  );
};

export default HelpTopics;
